// /var/www/afra/192.testproject.work/maracar8/0.8.2/myapp/src/pages/ExploreIdeasLab.js
import React, { useState, useEffect, useCallback } from 'react'; // เพิ่ม useCallback
import Sidebar from './Sidebar';
import Header from '../components/Header';
import IdeasLabView from '../components/Content/IdeasLabView';
import IdeasLabForm from '../components/Content/IdeasLabForm';
import { ProgressSpinner } from 'primereact/progressspinner';
import 'primereact/resources/themes/saga-blue/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import '../css/Member.css';
import { fetchIdeasLab, insertIdeasLab, updateIdeasLab, deleteIdeasLab } from '../utils/ExploreIdeasLabHandlers';
import { useNavigate, useLocation } from 'react-router-dom';

const ExploreIdeasLab = () => {
  const [ideas, setIdeas] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [pageLength, setPageLength] = useState(10);
  // Initialize currentPage from URL or default to 1
  const location = useLocation();
  const navigate = useNavigate();
  const getPageFromUrl = () => {
    const queryParams = new URLSearchParams(location.search);
    const pageFromUrl = parseInt(queryParams.get('page'), 10);
    return pageFromUrl && pageFromUrl > 0 ? pageFromUrl : 1;
  };
  const [currentPage, setCurrentPage] = useState(getPageFromUrl()); // <-- ตั้งค่าเริ่มต้นจาก URL

  const [showForm, setShowForm] = useState(false);
  const [formMode, setFormMode] = useState('add');
  const [selectedIdea, setSelectedIdea] = useState(null);


  // --- Fetch Data Logic ---
  // ใช้ useCallback เพื่อให้ function fetchData ไม่ถูกสร้างใหม่ทุกครั้งที่ re-render ถ้า dependencies ไม่เปลี่ยน
  const fetchData = useCallback(async (page, length) => {
    console.log(`Fetching data for page: ${page}, length: ${length}`); // Debug log
    setIsLoading(true);
    try {
      const offset = (page - 1) * length;
      // *** ตรวจสอบ API Handler ของคุณ ***
      // ต้องแน่ใจว่า fetchIdeasLab รับ pageLength และ offset/page ถูกต้อง
      // และ response กลับมามีโครงสร้าง { results: [...], total_count: ... } จริงๆ
      const response = await fetchIdeasLab(length, offset); // หรืออาจจะเป็น fetchIdeasLab(page, length) ขึ้นกับ API ของคุณ
      console.log("API Response:", response); // Debug log response ที่ได้

      // ตรวจสอบ response ก่อน set state
      if (response && Array.isArray(response.results) && typeof response.total_count === 'number') {
          setIdeas(response.results);
          setTotalCount(response.total_count);
      } else {
          console.error("Invalid data structure received from fetchIdeasLab:", response);
          setIdeas([]); // ตั้งค่าเป็น array ว่างเมื่อข้อมูลไม่ถูกต้อง
          setTotalCount(0);
      }

    } catch (error) {
      console.error('Error fetching ideas:', error);
      setIdeas([]);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
    }
  }, []); // Dependencies ของ fetchData คือ [] เพราะมันรับ page/length เป็น argument

  // --- useEffect for Fetching Data ---
  // ทำงานเมื่อ currentPage หรือ pageLength (State) เปลี่ยน
  useEffect(() => {
    fetchData(currentPage, pageLength);
  }, [currentPage, pageLength, fetchData]); // ใส่ fetchData ใน dependencies ด้วย

  // --- useEffect for Syncing State with URL (on initial load/external URL change) ---
  // ทำงานเมื่อ location.search เปลี่ยน (เช่น เข้าหน้าเว็บด้วย URL ที่มี ?page=...)
  useEffect(() => {
    const pageFromUrl = getPageFromUrl();
    console.log(`URL changed. Page from URL: ${pageFromUrl}, Current State Page: ${currentPage}`);
    if (pageFromUrl !== currentPage) {
      console.log("Updating state from URL parameter.");
      setCurrentPage(pageFromUrl); // อัปเดต state ให้ตรงกับ URL
      // ไม่ต้อง fetch data ที่นี่ เพราะ useEffect ด้านบนจะทำงานเมื่อ currentPage เปลี่ยน
    }
  }, [location.search]); // <--- Dependency คือ location.search เท่านั้น

  // --- Handlers ---
  const refreshTable = useCallback(() => { // ใช้ useCallback ถ้า refreshTable ถูกส่งไป component ลูกหลายชั้น
    fetchData(currentPage, pageLength);
  }, [currentPage, pageLength, fetchData]);

  const handleInsert = async (data) => {
    // ... (เหมือนเดิม)
     try {
       await insertIdeasLab(data);
       // หลัง insert สำเร็จ อาจจะกลับไปหน้า 1 หรืออยู่ที่เดิมก็ได้
       // ถ้าต้องการกลับไปหน้า 1:
       // if (currentPage !== 1) {
       //   handlePageSelect(1); // ใช้ handler ใหม่
       // } else {
       //   refreshTable();
       // }
       // ถ้าต้องการอยู่ที่เดิม:
       refreshTable();
       closeForm();
     } catch (error) {
       console.error('Error inserting idea:', error);
       throw error; // Re-throw for potential handling in the form
     }
  };

  const handleUpdate = async (data) => {
    // ... (เหมือนเดิม)
     try {
       await updateIdeasLab(data);
       refreshTable(); // Refresh ข้อมูลหน้าปัจจุบัน
       closeForm();
     } catch (error) {
       console.error('Error updating idea:', error);
       throw error;
     }
  };

  const handleDelete = async (id) => {
    // ... (เหมือนเดิม)
     try {
       await deleteIdeasLab(id);
       // หลังลบ อาจจะต้องเช็คว่าหน้านั้นหมดข้อมูลหรือยัง ถ้าหมดอาจจะต้องไปหน้าก่อนหน้า
       // หรือ απλά refresh หน้าปัจจุบัน
       refreshTable();
     } catch (error) {
       console.error('Error deleting idea:', error);
       throw error;
     }
  };

  // Handler สำหรับเปลี่ยนจำนวนรายการต่อหน้า
  const handlePageLengthChange = (e) => {
    const newPageLength = parseInt(e.target.value, 10);
    setPageLength(newPageLength);
    // เมื่อเปลี่ยนจำนวนต่อหน้า ให้กลับไปหน้า 1 เสมอ
    if (currentPage !== 1) {
        handlePageSelect(1); // ใช้ handler ใหม่
    } else {
        // ถ้าอยู่หน้า 1 อยู่แล้ว แค่เปลี่ยน pageLength state ก็พอ
        // useEffect[currentPage, pageLength] จะทำงานเองถ้า pageLength เปลี่ยน
        // แต่ถ้าต้องการ force fetch ทันที:
        // fetchData(1, newPageLength);
    }
  };

  // Handler หลักสำหรับการเปลี่ยนหน้า (จะถูกเรียกจาก IdeasLabView)
  const handlePageSelect = (newPage) => {
    if (newPage !== currentPage) {
      console.log(`Page selected: ${newPage}. Updating state and URL.`);
      setCurrentPage(newPage); // 1. อัปเดต State
      navigate(`/explore-ideas-lab?page=${newPage}`); // 2. อัปเดต URL
      // useEffect[currentPage, pageLength] จะทำงานเพื่อ fetch ข้อมูล
    }
  };


  const openForm = (mode, idea = null) => {
    setFormMode(mode);
    setSelectedIdea(idea);
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setSelectedIdea(null);
  };

  const handleSave = async (data) => {
    if (formMode === 'add') {
      await handleInsert(data);
    } else {
      await handleUpdate(data);
    }
  };

  const handleHeaderBack = () => {
    if (showForm) {
      closeForm();
    } else {
      navigate(-1);
    }
  };


  return (
    <div className="dashboard">
      <Sidebar />
      <div className="dashboard-main">
        <Header
          onBack={handleHeaderBack}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery} // อาจจะต้องเพิ่ม logic การ search ให้กระทบ fetchData และ URL ด้วย
          placeholderText="Search ideas..."
          refreshData={refreshTable}
        />
        <div className="p-datatable-details">
          <div className="member-table-container">
            {isLoading ? (
              <div className="text-center my-4">
                <ProgressSpinner
                  style={{ width: '50px', height: '50px' }}
                  strokeWidth="8"
                  fill="transparent"
                  animationDuration=".5s"
                />
              </div>
            ) : showForm ? (
              <IdeasLabForm
                ideaData={selectedIdea}
                mode={formMode}
                onCancel={closeForm}
                onSave={handleSave} // ส่ง handleSave ไป
              />
            ) : (
              <IdeasLabView
                headers={ideas}
                // setHeaders={setIdeas} // ไม่ควรส่ง setHeaders ลงไปตรงๆ ให้จัดการผ่าน refreshData
                totalCount={totalCount}
                // searchQuery={searchQuery} // ส่งไปถ้า IdeasLabView ใช้แสดงผล
                pageLength={pageLength}
                currentPage={currentPage} // ส่ง State ปัจจุบันไปแสดงผล
                // setCurrentPage={setCurrentPage} // ไม่ส่ง setCurrentPage โดยตรงแล้ว
                handlePageLengthChange={handlePageLengthChange} // ส่ง handler เปลี่ยนจำนวนต่อหน้า
                refreshData={refreshTable} // ส่ง handler refresh
                // --- เปลี่ยน Prop สำหรับการเปลี่ยนหน้า ---
                onPageSelect={handlePageSelect} // ส่ง handler ใหม่ที่จัดการทั้ง state และ URL
                // handleDelete={handleDelete} // ส่ง handler การลบไปให้ IdeasLabView จัดการ (ถ้าปุ่มลบอยู่ในนั้น)
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExploreIdeasLab;